import React, { useEffect } from 'react';
import { useI18n } from '../i18n';

/**
 * SeoHead synchronizes document metadata (title, description, Open Graph, Twitter cards, URLs, and lang attribute)
 * whenever the user or system switches the active locale.
 */
export const SeoHead: React.FC = () => {
  const { locale, t } = useI18n();

  useEffect(() => {
    // 1. Sync Document Title
    if (t.seo?.title) {
      document.title = t.seo.title;
    }

    // 2. Sync HTML lang attribute
    document.documentElement.lang = locale;

    // Helper to safely set meta attribute content
    const setMetaTag = (selector: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(selector);
      if (meta) {
        meta.setAttribute('content', content);
      }
    };

    // 3. Sync Meta Description & Keywords
    if (t.seo?.description) {
      setMetaTag('meta[name="description"]', t.seo.description);
    }
    if (t.seo?.keywords) {
      setMetaTag('meta[name="keywords"]', t.seo.keywords);
    }

    // 4. Sync Open Graph Meta
    if (t.seo?.ogTitle) {
      setMetaTag('meta[property="og:title"]', t.seo.ogTitle);
    }
    if (t.seo?.ogDescription) {
      setMetaTag('meta[property="og:description"]', t.seo.ogDescription);
    }

    // 5. Sync Twitter Card Meta
    if (t.seo?.ogTitle) {
      setMetaTag('meta[name="twitter:title"]', t.seo.ogTitle);
    }
    if (t.seo?.ogDescription) {
      setMetaTag('meta[name="twitter:description"]', t.seo.ogDescription);
    }

    // 6. Sync OG Locale
    const localeMap: Record<string, string> = {
      en: 'en_US',
      tr: 'tr_TR',
      es: 'es_ES',
    };
    setMetaTag('meta[property="og:locale"]', localeMap[locale] || 'en_US');

    // 7. Sync Canonical / Open Graph localized URL
    const baseUrl = 'https://ai-gateway.workouse.com/';
    const localizedUrl = locale === 'en' ? baseUrl : `${baseUrl}?lang=${locale}`;
    setMetaTag('meta[property="og:url"]', localizedUrl);
    setMetaTag('meta[name="twitter:url"]', localizedUrl);
  }, [locale, t]);

  return null;
};
